// NOTE: Budgets, geometry and tile defaults for the map engine.
//
// The MAX_MAP_* budgets below are mirrored in
// apps/nsai/server/src/chat/constant/chat-system-prompts.constants.ts, because
// the server carries no workspace dependency on this package — the same
// arrangement the chart and diagram budgets live under. Change a budget here
// and you must change it there too, or the model is told one limit and
// validated against another, and the block falls back to raw JSON in the
// transcript.

// NOTE: Past this many pins a map stops answering "where is it" and starts
// being a scatter plot with a basemap. The overflow is reported back to the
// model so it can aggregate or split rather than ship an unreadable figure.
export const MAX_MAP_MARKERS = 24;

// NOTE: Same reasoning as the diagram engine's focal cap: the accent is
// editorial, not a status flag, and marking six pins focal marks none.
export const MAX_MAP_FOCAL_MARKERS = 2;

export const MIN_MAP_ROUTE_POINTS = 2;
export const MAX_MAP_ROUTE_POINTS = 64;
export const MIN_MAP_AREA_POINTS = 3;
export const MAX_MAP_AREA_POINTS = 64;

// NOTE: NSAI-5931 — the reason given for a block whose source is not JSON at
// all. Written like the schema's own messages, to say what to fix, and fixed
// rather than taken from JSON.parse, whose wording differs between the browser
// engine and Hermes on mobile.
export const MAP_INVALID_JSON_ERROR =
  "The map is not valid JSON — check for a missing bracket, quote or comma";

export const MAX_MAP_LABEL_LENGTH = 60;
export const MAX_MAP_SUBLABEL_LENGTH = 48;
export const MAX_MAP_TITLE_LENGTH = 80;
export const MAX_MAP_SUBTITLE_LENGTH = 140;

// NOTE: Shared by the projection and the viewport rather than redefined in
// each — they have to agree, and two copies of a constant are two chances for
// them not to.
export const DEGREES_IN_CIRCLE = 360;
export const HALF_CIRCLE = 180;

export const MAP_LATITUDE_MIN = -90;
export const MAP_LATITUDE_MAX = 90;
export const MAP_LONGITUDE_MIN = -180;
export const MAP_LONGITUDE_MAX = 180;

// NOTE: Web Mercator cannot represent the poles — the projection runs to
// infinity there — so it is cut at the latitude that makes the world square.
// Every projection clamps to this rather than rejecting the coordinate: a
// research station at 88°N is a real answer, and drawing it at the top edge is
// better than refusing the map.
export const MAX_MERCATOR_LATITUDE = 85.0511287798066;

// NOTE: 256 is the XYZ standard. It is not a rendering preference: it is the
// size the {z}/{x}/{y} scheme is defined in, so it decides the tile maths, not
// how large a tile looks.
export const MAP_TILE_SIZE = 256;

// NOTE: Zoom 0 is the whole planet in one 256px tile, which is never the right
// answer inside a chat message. 19 is what a standard OpenStreetMap raster
// endpoint serves; a deployment whose own server stops earlier lowers it.
export const MAP_MIN_ZOOM = 1;
export const MAP_MAX_ZOOM = 19;

// NOTE: A single marker has no extent to fit, so the fit has nothing to solve
// for. This is the "here it is" zoom — a neighbourhood, close enough to place
// the pin on a street and wide enough to show which part of town it is in.
export const MAP_SINGLE_MARKER_ZOOM = 14;

// NOTE: Keeps a pin at the edge of the bounds from being drawn half outside the
// frame, and leaves room for its label.
export const MAP_FIT_PADDING = 56;

// NOTE: The size the engine assumes when a caller cannot measure one — mobile
// before layout, and any server-side render. Web passes its measured width.
export const MAP_DEFAULT_SIZE = { width: 640, height: 400 } as const;

export const MAP_MIN_HEIGHT = 220;
export const MAP_MAX_HEIGHT = 520;

// NOTE: A hard ceiling on tiles per view. The grid is derived from the viewport,
// so a pathological size (a very tall container, a zoom the caller forced)
// cannot fan one map out into hundreds of requests.
export const MAX_MAP_TILES = 96;

// NOTE: Required by the Open Database Licence wherever the tiles are shown.
// It is not decoration and must not be hidden behind a hover or a menu. The
// wording is the one OpenStreetMap's copyright page requires; "© OpenStreetMap"
// alone leaves out the contributors the licence credits.
export const DEFAULT_MAP_TILE_ATTRIBUTION = "© OpenStreetMap contributors";

export const MAP_ATTRIBUTION_HREF = "https://www.openstreetmap.org/copyright";

// NOTE: Pin and overlay geometry, in the SVG units both renderers draw in.
// These live here rather than in each app because they are not a styling
// preference on either platform — the same spec has to produce the same figure
// on web and on mobile, and two copies of a pin path drift the moment one is
// tweaked. What stays per-platform is what genuinely differs: the aspect ratio
// (a phone column is not a desktop panel), the web's referrer policy and pan
// threshold, the mobile fallback width.
//
// The path is anchored at its TIP, not its centre: a map pin points at a
// coordinate, and centring it would put the place it names half a pin north.
export const MAP_PIN_PATH =
  "M 0 0 C -6 -9, -11 -13, -11 -18 A 11 11 0 1 1 11 -18 C 11 -13 6 -9 0 0 Z";

export const MAP_PIN_HEAD_Y = -18;
export const MAP_PIN_HEAD_RADIUS = 11;
export const MAP_PIN_HALO_WIDTH = 2;
export const MAP_FOCAL_PIN_SCALE = 1.25;
export const MAP_PIN_INDEX_FONT_SIZE = 11;

export const MAP_ROUTE_WIDTH = 3;
export const MAP_ROUTE_HALO_WIDTH = 6;
export const MAP_AREA_STROKE_WIDTH = 2;

export const MAP_ZOOM_STEP = 1;
